import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  CursorArrowRaysIcon,
  GlobeAltIcon,
  MapPinIcon,
  BuildingOffice2Icon,
  ClockIcon,
  ArrowsRightLeftIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchEngagementOverview, fetchEngagementProducts } from '../../services/apiService';
import ProductEngagementDrawer from './ProductEngagementDrawer';
import {
  Card,
  EmptyState,
  Delta,
  Kpi,
  RankedBars,
  CantonTileMap,
  WeekHourHeatmap,
  VIEWS_COLOR,
  VISITORS_COLOR,
  cantonName,
  countryName,
  flagEmoji,
  formatNum,
} from './engagementUi';

// Order of the source filter dropdown (most useful first).
export const SOURCE_KEYS = [
  'all_products', 'bestsellers', 'discounts', 'search', 'category', 'campaign',
  'home', 'home_bestsellers', 'home_discounts', 'related', 'cart', 'favorites',
  'orders', 'direct', 'external', 'other',
];

const PAGE_SIZE = 15;
const EXPORT_MAX_ROWS = 2000;

const SORTS = ['views', 'visitors', 'carts', 'cartRate'];

/**
 * "Product engagement" — which shop products get opened, from which list,
 * country and (for Switzerland) canton/city. Every breakdown is a filter:
 * clicking a source, country or canton re-scopes the whole section.
 */
export default function ProductEngagementSection({ from, to }) {
  const { t } = useLanguage();
  const tt = useCallback((k, r) => t(`statistics.engagement.${k}`, r), [t]);
  const sourceLabel = useCallback((k) => tt(`sources.${k}`), [tt]);

  // Drill-down filters.
  const [source, setSource] = useState(null);
  const [country, setCountry] = useState(null);
  const [region, setRegion] = useState(null);
  const filters = useMemo(
    () => ({ from, to, source, country: region ? null : country, region }),
    [from, to, source, country, region]
  );

  // Overview.
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(false);

  // Product table.
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sort, setSort] = useState('views');
  const [page, setPage] = useState(0);
  const [products, setProducts] = useState({ items: [], total: 0 });
  const [productsLoading, setProductsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [openCode, setOpenCode] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  // Back to page 1 whenever what's listed changes.
  useEffect(() => setPage(0), [filters, debouncedQuery, sort]);

  useEffect(() => {
    if (!from || !to) return undefined;
    let cancelled = false;
    setOverviewLoading(true);
    setOverviewError(false);
    fetchEngagementOverview(filters)
      .then((res) => !cancelled && setOverview(res?.data || null))
      .catch(() => !cancelled && setOverviewError(true))
      .finally(() => !cancelled && setOverviewLoading(false));
    return () => { cancelled = true; };
  }, [filters, from, to]);

  // Ignore out-of-order responses when the admin types or pages quickly.
  const productsReq = useRef(0);
  useEffect(() => {
    if (!from || !to) return;
    const req = ++productsReq.current;
    setProductsLoading(true);
    fetchEngagementProducts({ ...filters, q: debouncedQuery, sort, limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((res) => {
        if (req !== productsReq.current) return;
        setProducts({ items: res?.data?.products || [], total: res?.data?.total || 0 });
      })
      .catch(() => req === productsReq.current && setProducts({ items: [], total: 0 }))
      .finally(() => req === productsReq.current && setProductsLoading(false));
  }, [filters, debouncedQuery, sort, page, from, to]);

  const unknown = t('statistics.countries.unknown');

  const sourceRows = useMemo(
    () => (overview?.bySource || []).map((s) => ({ key: s.source, label: sourceLabel(s.source), value: s.views })),
    [overview, sourceLabel]
  );
  const countryRows = useMemo(
    () =>
      (overview?.byCountry || []).map((c) => ({
        key: c.country,
        label: countryName(c.country, unknown),
        prefix: c.country === 'Unknown' ? '🏳️' : flagEmoji(c.country),
        value: c.views,
      })),
    [overview, unknown]
  );
  const cantonRows = useMemo(
    () => (overview?.byRegion || []).map((r) => ({ key: r.region, label: cantonName(r.region, unknown), value: r.views })),
    [overview, unknown]
  );
  const swissViews = useMemo(
    () => (overview?.byCountry || []).find((c) => c.country === 'CH')?.views || 0,
    [overview]
  );

  const dayLabels = useMemo(() => tt('days').split(','), [tt]);

  const clearFilters = () => { setSource(null); setCountry(null); setRegion(null); };
  const selectCountry = (c) => { setRegion(null); setCountry(c); };
  const selectRegion = (r) => { setRegion(r); if (r) setCountry(null); };

  const chips = [
    source && { key: 'source', label: `${tt('filters.source')}: ${sourceLabel(source)}`, clear: () => setSource(null) },
    country && !region && { key: 'country', label: `${flagEmoji(country)} ${countryName(country, unknown)}`, clear: () => setCountry(null) },
    region && { key: 'region', label: `🇨🇭 ${cantonName(region, unknown)}`, clear: () => setRegion(null) },
  ].filter(Boolean);

  const totals = overview?.totals || {};
  const deltas = overview?.deltas || {};
  const vsPrev = t('statistics.vsPrev');
  const newLabel = tt('new');

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const all = [];
      for (let offset = 0; offset < EXPORT_MAX_ROWS; offset += 100) {
        const res = await fetchEngagementProducts({ ...filters, q: debouncedQuery, sort, limit: 100, offset });
        const batch = res?.data?.products || [];
        all.push(...batch);
        if (batch.length < 100 || all.length >= (res?.data?.total || 0)) break;
      }
      const header = [
        'Rank', 'Pharmacode', 'Product', 'Brand', 'Company', 'Bestseller', 'Discount',
        'Views', 'Views (prev. period)', 'Trend %', 'Unique visitors', 'Visitors who added to cart',
        'View → cart %', 'Units sold', 'Orders', 'Revenue (CHF)', 'Top country', 'Top canton', 'Sources',
      ];
      const rows = all.map((p, i) => [
        i + 1, Number(p.pharmacode), p.name || '', p.brand || '', p.company || '',
        p.isBestseller ? 'yes' : '',
        p.discount ? (p.discount.kind === 'percent' ? `-${p.discount.amount}%` : `CHF ${p.discount.amount}`) : '',
        p.views, p.prevViews, p.trend == null ? '' : p.trend / 100, p.visitors, p.cartVisitors,
        p.cartRate / 100, p.purchases?.units || 0, p.purchases?.orders || 0, p.purchases?.revenue || 0,
        p.topCountry ? countryName(p.topCountry.code, unknown) : '',
        p.topRegion ? cantonName(p.topRegion.code, unknown) : '',
        (p.sources || []).map((s) => `${sourceLabel(s.source)} ${s.views}`).join(', '),
      ]);
      const scope = chips.map((c) => c.label).join(' · ');
      const ws = XLSX.utils.aoa_to_sheet([
        ['Product engagement report'],
        [`${from} → ${to}${scope ? `  ·  ${scope}` : ''}${debouncedQuery ? `  ·  "${debouncedQuery}"` : ''}`],
        [],
        header,
        ...rows,
      ]);
      ws['!cols'] = [6, 12, 44, 18, 24, 10, 10, 9, 12, 9, 12, 14, 12, 10, 8, 13, 16, 18, 48].map((wch) => ({ wch }));
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: header.length - 1 } },
      ];
      rows.forEach((_, i) => {
        const r = i + 5;
        for (const col of ['J', 'M']) if (ws[`${col}${r}`] && ws[`${col}${r}`].v !== '') ws[`${col}${r}`].z = '0.0%';
        if (ws[`P${r}`]) ws[`P${r}`].z = '#,##0.00';
      });
      ws['!autofilter'] = { ref: `A4:${XLSX.utils.encode_col(header.length - 1)}${rows.length + 4}` };
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Product engagement');
      XLSX.writeFile(wb, `product-engagement-${from}-to-${to}.xlsx`, { compression: true });
    } catch (err) {
      console.error('Engagement export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const SortHeader = ({ id, children, className = '' }) => (
    <th className={`px-3 py-2 font-medium text-right ${className}`}>
      <button
        type="button"
        onClick={() => setSort(id)}
        className={`inline-flex items-center gap-0.5 hover:text-gray-900 ${sort === id ? 'text-indigo-700' : ''}`}
      >
        {children}
        {sort === id ? <ChevronDownIcon className="h-3 w-3" /> : <ChevronUpDownIcon className="h-3 w-3 opacity-50" />}
      </button>
    </th>
  );

  const pages = Math.max(Math.ceil(products.total / PAGE_SIZE), 1);

  return (
    <section className="space-y-4" aria-labelledby="engagement-title">
      {/* Header + filters */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 pt-2">
        <div>
          <h2 id="engagement-title" className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <CursorArrowRaysIcon className="h-5 w-5 text-indigo-600" />
            {tt('title')}
          </h2>
          <p className="text-sm text-gray-500">{tt('subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium pl-2.5 pr-1 py-1">
              {c.label}
              <button type="button" onClick={c.clear} className="rounded-full p-0.5 hover:bg-indigo-100" aria-label={tt('filters.clear')}>
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          {chips.length > 1 && (
            <button type="button" onClick={clearFilters} className="text-xs font-semibold text-gray-500 hover:text-gray-700">
              {tt('filters.clearAll')}
            </button>
          )}
          <select
            value={source || ''}
            onChange={(e) => setSource(e.target.value || null)}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white"
            aria-label={tt('filters.source')}
          >
            <option value="">{tt('filters.allSources')}</option>
            {SOURCE_KEYS.map((k) => (
              <option key={k} value={k}>{sourceLabel(k)}</option>
            ))}
          </select>
        </div>
      </div>

      {overviewError && (
        <div className="rounded-lg bg-red-50 text-red-700 text-sm px-4 py-3">{tt('error')}</div>
      )}

      {/* KPIs */}
      <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity ${overviewLoading ? 'opacity-60' : ''}`}>
        <Kpi
          label={tt('kpi.views')}
          value={formatNum(totals.views)}
          hint={tt('kpi.viewsHint')}
          delta={<Delta value={deltas.views} newLabel={newLabel} title={vsPrev} />}
        />
        <Kpi
          label={tt('kpi.visitors')}
          value={formatNum(totals.visitors)}
          hint={totals.visitors ? tt('kpi.perVisitor', { n: (totals.views / totals.visitors).toFixed(1) }) : tt('kpi.visitorsHint')}
          delta={<Delta value={deltas.visitors} newLabel={newLabel} title={vsPrev} />}
        />
        <Kpi
          label={tt('kpi.products')}
          value={formatNum(totals.products)}
          hint={tt('kpi.productsHint')}
          delta={<Delta value={deltas.products} newLabel={newLabel} title={vsPrev} />}
        />
        <Kpi
          label={tt('kpi.cartRate')}
          value={`${(totals.cartRate || 0).toFixed(1)}%`}
          hint={tt('kpi.cartRateHint', { n: formatNum(totals.cartVisitors) })}
          delta={<Delta value={deltas.cartRate} unit="pp" title={vsPrev} />}
        />
      </div>

      {/* Trend + sources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={tt('charts.overTime')} icon={CursorArrowRaysIcon} className="lg:col-span-2">
          {(overview?.timeline || []).some((d) => d.views) ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={overview.timeline} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="eng-views" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={VIEWS_COLOR} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={VIEWS_COLOR} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                <Tooltip formatter={(v) => formatNum(v)} labelStyle={{ fontWeight: 600 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="views" name={tt('kpi.views')} stroke={VIEWS_COLOR} strokeWidth={2} fill="url(#eng-views)" />
                <Area type="monotone" dataKey="visitors" name={tt('kpi.visitors')} stroke={VISITORS_COLOR} strokeWidth={2} fill="none" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState>{t('statistics.noData')}</EmptyState>
          )}
        </Card>
        <Card title={tt('charts.sources')} icon={ArrowsRightLeftIcon}>
          <RankedBars rows={sourceRows} selected={source} onSelect={setSource} emptyLabel={t('statistics.noData')} valueLabel={tt('unit')} />
        </Card>
      </div>

      {/* Geography */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={tt('charts.countries')} icon={GlobeAltIcon}>
          <RankedBars rows={countryRows} selected={region ? 'CH' : country} onSelect={selectCountry} emptyLabel={t('statistics.noData')} valueLabel={tt('unit')} />
        </Card>
        <Card
          title={tt('charts.cantons')}
          icon={MapPinIcon}
          action={swissViews > 0 && <span className="text-[11px] text-gray-400">{tt('swissViews', { n: formatNum(swissViews) })}</span>}
        >
          {cantonRows.length ? (
            <>
              <CantonTileMap rows={overview.byRegion} selected={region} onSelect={selectRegion} unitLabel={tt('unit')} />
              <div className="mt-4">
                <RankedBars rows={cantonRows} selected={region} onSelect={selectRegion} limit={5} valueLabel={tt('unit')} />
              </div>
            </>
          ) : (
            <EmptyState>{tt('noSwissData')}</EmptyState>
          )}
        </Card>
        <Card title={tt('charts.cities')} icon={BuildingOffice2Icon}>
          {(overview?.byCity || []).length ? (
            <ol className="divide-y divide-gray-100">
              {overview.byCity.slice(0, 12).map((c, i) => (
                <li key={`${c.city}-${c.region}`} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="w-5 text-xs font-bold text-gray-400 tabular-nums">{i + 1}</span>
                    <span className="truncate text-gray-700">{c.city}</span>
                    {c.region && (
                      <button
                        type="button"
                        onClick={() => selectRegion(c.region)}
                        className="shrink-0 text-[10px] font-semibold rounded bg-gray-100 text-gray-600 px-1.5 py-0.5 hover:bg-indigo-50 hover:text-indigo-700"
                        title={cantonName(c.region, unknown)}
                      >
                        {c.region}
                      </button>
                    )}
                  </span>
                  <span className="shrink-0 tabular-nums text-gray-900 font-semibold">
                    {formatNum(c.views)}
                    <span className="ml-1.5 text-[10px] font-normal text-gray-500">{tt('visitorsShort', { n: formatNum(c.visitors) })}</span>
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <EmptyState>{tt('noSwissData')}</EmptyState>
          )}
          <p className="mt-3 text-[10px] text-gray-400">{tt('geoNote')}</p>
        </Card>
      </div>

      <Card title={tt('charts.heatmap')} icon={ClockIcon}>
        {(overview?.heatmap || []).length ? (
          <WeekHourHeatmap data={overview.heatmap} dayLabels={dayLabels} unitLabel={tt('unit')} />
        ) : (
          <EmptyState>{t('statistics.noData')}</EmptyState>
        )}
      </Card>

      {/* Product table */}
      <Card
        title={tt('table.title')}
        icon={CursorArrowRaysIcon}
        action={
          <button
            type="button"
            onClick={exportXlsx}
            disabled={exporting || !products.total}
            className="px-3 py-1.5 text-xs rounded-lg font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {exporting ? t('statistics.listModal.exporting') : tt('table.export')}
          </button>
        }
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tt('table.search')}
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-1.5 text-sm"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white sm:hidden"
            aria-label={tt('table.sortBy')}
          >
            {SORTS.map((s) => <option key={s} value={s}>{tt(`table.sort.${s}`)}</option>)}
          </select>
        </div>

        <div className={`overflow-x-auto -mx-5 transition-opacity ${productsLoading ? 'opacity-60' : ''}`}>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="pl-5 pr-2 py-2 font-medium text-left w-8">#</th>
                <th className="px-3 py-2 font-medium text-left">{tt('table.product')}</th>
                <SortHeader id="views">{tt('kpi.views')}</SortHeader>
                <SortHeader id="visitors" className="hidden md:table-cell">{tt('table.visitors')}</SortHeader>
                <SortHeader id="carts" className="hidden md:table-cell">{tt('table.carts')}</SortHeader>
                <SortHeader id="cartRate">{tt('table.cartRate')}</SortHeader>
                <th className="px-3 py-2 font-medium text-right hidden lg:table-cell">{tt('table.sold')}</th>
                <th className="px-3 py-2 font-medium text-left hidden lg:table-cell">{tt('table.topSource')}</th>
                <th className="pl-3 pr-5 py-2 font-medium text-left hidden xl:table-cell">{tt('table.topLocation')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.items.map((p, i) => {
                const topSource = p.sources?.[0];
                const topShare = topSource && p.views ? Math.round((topSource.views / p.views) * 100) : 0;
                return (
                  <tr
                    key={p.pharmacode}
                    onClick={() => setOpenCode(p.pharmacode)}
                    className="cursor-pointer hover:bg-indigo-50/40"
                  >
                    <td className="pl-5 pr-2 py-2.5 text-xs font-bold text-gray-400 tabular-nums">{page * PAGE_SIZE + i + 1}</td>
                    <td className="px-3 py-2.5 max-w-[320px]">
                      <p className="font-medium text-gray-900 truncate" title={p.name || ''}>
                        {p.name || <span className="italic text-gray-400">{tt('unknownProduct')}</span>}
                      </p>
                      <p className="text-[11px] text-gray-500 flex items-center gap-1.5 flex-wrap">
                        <span className="tabular-nums">{p.pharmacode}</span>
                        {p.brand && <span>· {p.brand}</span>}
                        {p.isBestseller && <span className="rounded bg-amber-50 text-amber-800 px-1.5 font-semibold">★ {tt('badges.bestseller')}</span>}
                        {p.discount && (
                          <span className="rounded bg-rose-50 text-rose-700 px-1.5 font-semibold">
                            {p.discount.kind === 'percent' ? `−${p.discount.amount}%` : tt('badges.discount')}
                          </span>
                        )}
                        {p.isDeleted && <span className="rounded bg-gray-100 text-gray-600 px-1.5">{tt('badges.deleted')}</span>}
                      </p>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <span className="font-semibold text-gray-900">{formatNum(p.views)}</span>
                      <div className="mt-0.5"><Delta value={p.trend} newLabel={newLabel} title={vsPrev} /></div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700 hidden md:table-cell">{formatNum(p.visitors)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700 hidden md:table-cell">{formatNum(p.cartVisitors)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{p.cartRate.toFixed(1)}%</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700 hidden lg:table-cell">{formatNum(p.purchases?.units)}</td>
                    <td className="px-3 py-2.5 text-gray-700 hidden lg:table-cell whitespace-nowrap">
                      {topSource ? (
                        <>
                          {sourceLabel(topSource.source)}
                          <span className="ml-1 text-[10px] text-gray-500">{topShare}%</span>
                        </>
                      ) : '—'}
                    </td>
                    <td className="pl-3 pr-5 py-2.5 text-gray-700 hidden xl:table-cell whitespace-nowrap">
                      {p.topCountry ? (
                        <>
                          {flagEmoji(p.topCountry.code === 'Unknown' ? null : p.topCountry.code)}{' '}
                          {p.topRegion ? cantonName(p.topRegion.code, unknown) : countryName(p.topCountry.code, unknown)}
                        </>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!productsLoading && !products.items.length && <EmptyState>{t('statistics.noData')}</EmptyState>}
        </div>

        {products.total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
            <span>
              {t('statistics.listModal.showing', {
                from: page * PAGE_SIZE + 1,
                to: Math.min((page + 1) * PAGE_SIZE, products.total),
                total: formatNum(products.total),
              })}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 0))}
                disabled={page === 0}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                aria-label={t('statistics.listModal.prev')}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <span className="px-2 tabular-nums">{t('statistics.listModal.page', { page: page + 1, pages })}</span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pages - 1))}
                disabled={page >= pages - 1}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                aria-label={t('statistics.listModal.next')}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </Card>

      <ProductEngagementDrawer
        pharmacode={openCode}
        filters={filters}
        onClose={() => setOpenCode(null)}
        sourceLabel={sourceLabel}
        dayLabels={dayLabels}
      />
    </section>
  );
}
